import * as React from 'react';
import { styled, alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch, { SwitchProps } from '@mui/material/Switch';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import ListItemButton from '@mui/material/ListItemButton';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import Co2Icon from '@mui/icons-material/Co2';
import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import FolderIcon from '@mui/icons-material/Folder';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ButtonBase from '@mui/material/ButtonBase';
import Logo from './pics/Logo_White-01.svg';
import colorLogo from './pics/AmbientLogo_Colour-01.svg';
import Avatar from '@mui/material/Avatar';
import Button from "@mui/material/Button";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from "@mui/material/Typography";
import {
  useNavigate,
   Outlet
} from "react-router-dom";
import { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { getFirestore, getDoc, doc, collection, onSnapshot, query, where, limit, orderBy } from 'firebase/firestore'
import { getAuth, signOut } from "firebase/auth";
import emailjs from "emailjs-com";

// Connect to current firebase project
const firebaseApp = initializeApp({
  apiKey: "AIzaSyBamRPS6NxxPg7lplFrxZd36fLWg9ZUTDY",
  authDomain: "ambient-systems.firebaseapp.com",
  databaseURL: "https://ambient-systems-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "ambient-systems",
  storageBucket: "ambient-systems.appspot.com",
  messagingSenderId: "1001740093061",
  appId: "1:1001740093061:web:b516ca06c0e80199b90bec"
});
const storage = getStorage(firebaseApp);
const firestore = getFirestore(firebaseApp);

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(110),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const drawerWidth = 220;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

const mdTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#031c4a',
      paper: '#031c4a',
    },
    error: {
      main: '#060c43',
    },
    warning: {
      main: '#00e676',
    },
    success: {
      main: '#ffb74d',
    },
  },
});

const ndTheme = createTheme({
  palette: {
    mode: 'light',
  }
});

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#fff',
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
    borderRadius: 20 / 2,
  },
}));

// Main function of this page
function DashboardContent() {
  const [fff, setFFF] = React.useState("dark");
  const [open, setOpen] = React.useState(true);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorEl2, setAnchorEl2] = React.useState(null);
  const [anchorEl3, setAnchorEl3] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMenuOpen2 = Boolean(anchorEl2);
  const isMenuOpen3 = Boolean(anchorEl3);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleInfoMenuOpen = (event) => {
    setAnchorEl2(event.currentTarget);
  };
  const handleMessageMenuOpen = (event) => {
    setAnchorEl3(event.currentTarget);
    setNum(0)
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuClose2 = () => {
    setAnchorEl2(null);
  };
  const handleMenuClose3 = () => {
    setAnchorEl3(null);
  };
  const handleSignOut = () => {
    const auth = getAuth();
    auth.signOut()
    window.localStorage.removeItem('access_token');
    window.location.reload();
  }
  
  /* Droplists of head bar buttons */
  /* Droplist of Message/First button */
  const [alerts, setAlerts] = useState([]);
  const [num, setNum] = useState(0);

  function formatDate() {
    var d = new Date(),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2) 
        month = '0' + month;
    if (day.length < 2) 
        day = '0' + day;

    return [year, month, day].join('-');
  }

  useEffect(() => {
    const alertQuery = query(
      collection(firestore, 'alerts'),
      where('email', '==', user_email),
      where('date', '==', formatDate()),
      orderBy("time", "desc"),
      limit(5)
    );
      const unsubscribe = onSnapshot(alertQuery, snapshot => {
        setAlerts(snapshot.docs.map(doc => ({id: doc.id, data: doc.data() })))
        setNum(1)
      })

      return () => {
        unsubscribe()
      }
  }, [])

  const renderMessageMenu = (
    <Menu
      anchorEl={anchorEl3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen3}
      onClose={handleMenuClose3}
    >
    {alerts?.map((row) => (
      <MenuItem onClick={handleMenuClose3}>
        <ListItemIcon>
          <NotificationsIcon fontSize="small"/>
        </ListItemIcon>
        <ListItemText>
          {row.data.content}
        </ListItemText>
      </MenuItem>
    ))}
    </Menu>
  );

  /* Droplist of Information/Second button */
  const renderInfoMenu = (
    <Menu
      anchorEl={anchorEl2}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen2}
      onClose={handleMenuClose2}
    >
      <MenuItem onClick={handleMenuClose2}>
        <ListItemIcon>
          <CheckIcon fontSize="small"/>
        </ListItemIcon>
        <ListItemText>
          Your project is running well. 
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={handleMenuClose2}>
        <ListItemIcon>
          <CheckIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          All services are ready. 
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={handleMenuClose2}>
        <ListItemIcon>
          <CloseIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          Some information missed. 
        </ListItemText>
      </MenuItem>
    </Menu>
  );
  
  /* Droplist of Profile/Third button */
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}><ButtonBase href='Profile'>Profile</ButtonBase></MenuItem>
      <MenuItem onClick={handleMenuClose}><ButtonBase href='/Dashboard/Profile/Settings'>Settings</ButtonBase></MenuItem>
      <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
    </Menu>
  );

  /* Identify the current user */
  const user_email = window.localStorage.getItem('user_email');

  /* Set-up avatar */
  const [avatar, setAvatar] = useState([])

  const [payWall, setPayWall] = useState(false)
  useEffect(() => {
    const authQuery = query(
      collection(firestore, 'users'),
      where('username', '==', user_email),
    );
    onSnapshot(authQuery, snapshot => {
        const t = (snapshot.docs.map(doc => ({ data: doc.data().isUpgrade })))
        if (!t[0].data) {
            setPayWall(true)
        }
    })
  }, [])

  useEffect(() => {
    if (!user_email) {
      setAvatar('https://cdn.icon-icons.com/icons2/1378/PNG/512/avatardefault_92824.png')
    } else {
      const avatarRef = ref(storage, user_email+'/avatar.png')
      getDownloadURL(avatarRef)
      .then((url) => {
        setAvatar(url)
      })
      .catch((error) => {
        switch (error.code) {
          case 'storage/object-not-found':
            setAvatar('https://cdn.icon-icons.com/icons2/1378/PNG/512/avatardefault_92824.png')
            break;

          case 'storage/unknown':
            console.log("Unknown error!")
            break;
        }})
    }
  }, [])

  const [myTheme, setMyTheme] = useState(mdTheme)
  const [upgrade1, setUpgrade1] = useState(false)
  const [upgrade2, setUpgrade2] = useState(false)

  const handleUpgrade = () => {
    if (payWall) {
      setUpgrade1(true)
    } else {
      setUpgrade2(true)
    }
  }

  const closeUpgrade1 = () => {
    setUpgrade1(false)
  }

  const closeUpgrade2 = () => {
    setUpgrade2(false)
  }

  function sendRequest() {
    emailjs.send("teamService","template_cqyfsfh",{
      subject: "User requested to upgrade his version!",
      name: "Upgrade request",
      email: "from " + user_email,
      message: "Please modify his permission in the firestore \'users\' collection and check attribute \'isUpgrade\'",
    }, "r87tnpnYE2FXwhAzx");
    alert("Have sent request to our consultant! Will contact you soon. Thank you!")
  }

  return (
    <ThemeProvider theme={myTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />

        {/* Head bar layout */}
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: '24px', // keep right padding when drawer closed
              backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[200]
                : theme.palette.background.default,
            }}
          >

            {/* Search bar */}
            {/* <Search sx={{flexGrow: 0, borderRadius: 25, 
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.info.main
                  : theme.palette.error.main,
          }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search> */}

            {/* Placeholder */}
            <Box sx={{ flexGrow: 1 }} />

            <FormControlLabel
              control={<MaterialUISwitch defaultChecked onChange={() => myTheme === mdTheme? setMyTheme(ndTheme) : setMyTheme(mdTheme)} />}
            />

            <Button variant="contained" color="primary" onClick={handleUpgrade}>Upgrade to Pro</Button>
            <Box sx={{ width: 50 }}></Box>

            {/* Three functional buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              {/* Message/First button */}
              <IconButton size="large" aria-label="show 2 new mails" color="inherit" onClick={handleMessageMenuOpen}>
                <Badge badgeContent={num} color="info">
                  <NotificationsOutlinedIcon />
                </Badge>
              </IconButton>
              {/* Information/Second button */}
              {/* <IconButton
                size="large"
                aria-label="show 3 new notifications"
                onClick={handleInfoMenuOpen}
                color="inherit"
              >
                <Badge badgeContent={3} color="info">
                  <InfoOutlinedIcon />
                </Badge>
              </IconButton> */}
              {/* Profile/Third button */}
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                {/* <AccountCircleOutlinedIcon /> */}
                <Avatar src={avatar} alt='avatar' sx={{ width: 30, height: 30 }}></Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {/* {renderInfoMenu} */}
        {renderMessageMenu}
        {renderMenu}

            {/* Side bar layout */}
            <Drawer variant="permanent" open={open}>
              {/* Logo at the top left cornor */}
              <Toolbar
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: [1],
                  height: 130,
                }}
              >
                <ButtonBase href='/Dashboard/Welcome' fullWidth>
                  {myTheme === mdTheme? (<img src={Logo} alt="Logo" width={180} height={75}/>) : (<img src={colorLogo} alt="Logo" width={180} height={75}/>)}
                </ButtonBase>
              </Toolbar>
              <Divider />

              {/* Navigations to different pages */}
              <List component="nav">
                {/* Overview */}
                <ListItemButton href="/Dashboard/Overview" >
                    <ListItemIcon>
                      <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Overview" />
                </ListItemButton>

                {/* Goals */}
                {/* <ListItemButton  href="/Dashboard/Goal" >
                    <ListItemIcon>
                      <TrackChangesIcon />
                    </ListItemIcon>
                    <ListItemText primary="Goals" />
                </ListItemButton> */}

                {/* Data Engineering */}
                <ListItemButton href="/Dashboard/Engineering" >
                    <ListItemIcon>
                      <StorageIcon />
                    </ListItemIcon>
                    <ListItemText primary="Data Engineering" />
                </ListItemButton>

                {/* Carbon Measurement */}
                <ListItemButton href="/Dashboard/Carbon" >
                    <ListItemIcon>
                      <Co2Icon />
                    </ListItemIcon>
                    <ListItemText primary="Carbon Measure" />
                </ListItemButton>

                {/* Data Visualization */}
                <ListItemButton href="/Dashboard/Visualization" >
                    <ListItemIcon>
                      <BarChartIcon />
                    </ListItemIcon>
                    <ListItemText primary="Data Visualization" />
                </ListItemButton>

                {/* Machine Learning */}
                <ListItemButton  href="/Dashboard/ML" >
                    <ListItemIcon>
                      <AccountTreeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Machine Learning" />
                </ListItemButton>

                {/* Marketplace */}
                <ListItemButton  href="/Dashboard/Marketplace" >
                    <ListItemIcon>
                      <AddBusinessIcon />
                    </ListItemIcon>
                    <ListItemText primary="Marketplace" />
                </ListItemButton>

                {/* Profile */}
                <ListItemButton href="/Dashboard/Profile" >
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary="Profile" />
                </ListItemButton>
              </List>
            </Drawer>

        {/* Main space of page, which is blank in this page */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.error.main,
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Toolbar />
          <Outlet />
        </Box>

        {/* Two dialogs for upgrade choice */}
        <Dialog open={upgrade2} onClose={closeUpgrade2} maxWidth={"lg"} fullWidth={true}>
          <DialogContent>
            <Box sx={{ minHeight: 500 }} display="flex" justifyContent="center" alignItems="center">
              You are already in premium version. We sincerely appreciate to have you. Please enjoy!
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog open={upgrade1} onClose={closeUpgrade1} maxWidth={"lg"} fullWidth={true}>
          <DialogContent>
            <Box sx={{ minHeight: 500 }}>
              <Box sx={{ mt: 4, ml: 2 }}>
                <Typography variant="h5" style={{ fontWeight: 600 }}>Introducing our premium version:</Typography>
              </Box>
              <Box sx={{ mt: 5, ml: 2 }}>
                <Typography variant="h6">To upgrade your version, more than these functions below will be open to you:</Typography>
              </Box>
              <Box sx={{ mt: 4, ml: 10 }}>
                <Typography variant="h6" color="#FFD700">※ Data Augmentation: More than 10 open-source APIs and 5 database are open to you to enrich your data feeds.</Typography>
                <Typography variant="h6" color="#FFD700" sx={{ mt: 3 }}>※ ML Support: More than 8 ML techniques to help your situation, and full permission to customize and supervise.</Typography>
                <Typography variant="h6" color="#FFD700" sx={{ mt: 3 }}>※ Visualization: Show the features of your data on clear and pretty advanced ways that you can decide.</Typography>
                <Typography variant="h6" color="#FFD700" sx={{ mt: 3 }}>※ ... ...</Typography>
              </Box>
              <Box sx={{ mt: 4, ml: 2 }}>
                <Typography variant="h6">Our lovely price: TBD</Typography>
              </Box>
              <Box sx={{ mt: 4, ml: 2 }} display="flex" justifyContent="center" alignItems="center">
                <Button variant="contained" color="secondary" onClick={sendRequest}>Upgrade now</Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default function Dashboard() {
  /* Identify the current user */
  const user_email = window.localStorage.getItem('user_email');
  
  const navigate = useNavigate()

  const userCheck = async () => {
    const snapShot = await getDoc(doc(firestore, 'users/'+user_email))
    if (!snapShot.exists()) {
      navigate("/Onboard")
    }
  };

  useEffect(() => {
  const auth = getAuth();
  const token =  window.localStorage.getItem('access_token');
  if(!token || !user_email) navigate("/");
  else userCheck();
  },[])
    
  return <DashboardContent />;
}